// AuthProvider — token state + login flow for the static-login mode.
//
// Token lives in localStorage so a page refresh doesn't log the user
// out. (NOT memory-only — that would be slightly more XSS-resistant
// but completely useless for an SPA the operator refreshes a lot.
// XSS resistance comes from the API tightening down — see notes in
// client.ts.)
//
// 401 anywhere → token cleared → router re-renders → Login page
// shows. signIn() POSTs to /api/v1/login and stores the returned
// token. signOut() just clears the token.
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from "react";

/** The caller's own identity, from GET /api/v1/whoami. */
export interface Identity {
  subject: string;
  groups: string[];
  /** Runtime feature flag (server env BLUEROCK_MCP_SCAN). Gates whether the UI
   *  shows the MCP Scan nav item + route. Only rc38 (the host holding the
   *  trust-scan key) runs with this on; brdemo runs with it off. */
  mcpScanEnabled: boolean;
}

interface AuthState {
  token: string | null;
  /** null while the first whoami is in flight. */
  identity: Identity | null;
  /** True until whoami has resolved once, so guards don't redirect on a
   *  half-known identity — without this a refresh briefly looks like
   *  "not an admin" and bounces the user off an admin page. */
  identityLoading: boolean;
  /** "admin" | "viewer" | null. Derived from groups; the server is still
   *  the authority — this only decides what the UI OFFERS. */
  role: string | null;
  /** Runtime MCP-scan feature flag from whoami. Only gates the nav/route
   *  surface; the scan API stays independently gated server-side. */
  mcpScanEnabled: boolean;
  /** The account owes a password rotation. The server enforces this (the
   *  identity carries only `pwreset`, so every route but the change itself
   *  is refused); the UI honours it so the user sees a form instead of a
   *  wall of 403s. */
  mustChangePassword: boolean;
  signIn: (username: string, password: string) => Promise<void>;
  signOut: () => void;
  /** Re-read whoami — call after a rotation so the new role applies without
   *  forcing a re-login (the token is already promoted server-side). */
  refreshIdentity: () => Promise<void>;
  /** Called by the API client on any 401 — clears the token so the
   *  router redirects to /login on the next render. */
  onUnauthorized: () => void;
}

const GROUP_PASSWORD_RESET = "pwreset";

const Ctx = createContext<AuthState | null>(null);
const TOKEN_KEY = "bluerock.token";

export function AuthProvider({ children }: { children: ReactNode }) {
  const [token, setToken] = useState<string | null>(() =>
    localStorage.getItem(TOKEN_KEY),
  );
  const [identity, setIdentity] = useState<Identity | null>(null);
  const [identityLoading, setIdentityLoading] = useState<boolean>(
    () => localStorage.getItem(TOKEN_KEY) !== null,
  );

  // Keep localStorage in sync with the in-memory token.
  useEffect(() => {
    if (token) localStorage.setItem(TOKEN_KEY, token);
    else localStorage.removeItem(TOKEN_KEY);
  }, [token]);

  const fetchIdentity = useCallback(async (tok: string): Promise<Identity | null> => {
    try {
      const res = await fetch(`${import.meta.env.BASE_URL}api/v1/whoami`, {
        headers: { Authorization: `Bearer ${tok}` },
      });
      if (res.status === 401) {
        // The token itself is dead (expired/revoked) — clear it, same as
        // onUnauthorized below, so RequireAuth redirects to /login instead
        // of leaving the user stranded on a page whose own data calls will
        // just keep 401ing forever with no way back to sign-in.
        setToken(null);
        return null;
      }
      if (!res.ok) return null;
      const body = (await res.json()) as { subject?: string; groups?: string[]; mcp_scan_enabled?: boolean };
      return { subject: body.subject ?? "", groups: body.groups ?? [], mcpScanEnabled: body.mcp_scan_enabled === true };
    } catch {
      // Network failure is not "logged out" — leave the token alone and let
      // the next API call surface a real error.
      return null;
    }
  }, []);

  // Resolve identity whenever the token changes, including on first load with a
  // token restored from localStorage.
  useEffect(() => {
    let cancelled = false;
    if (!token) {
      setIdentity(null);
      setIdentityLoading(false);
      return;
    }
    setIdentityLoading(true);
    void fetchIdentity(token).then((id) => {
      if (cancelled) return;
      setIdentity(id);
      setIdentityLoading(false);
    });
    return () => {
      cancelled = true;
    };
  }, [token, fetchIdentity]);

  const refreshIdentity = useCallback(async () => {
    if (!token) return;
    const id = await fetchIdentity(token);
    if (id) setIdentity(id);
  }, [token, fetchIdentity]);

  const signIn = useCallback(async (username: string, password: string) => {
    const res = await fetch(`${import.meta.env.BASE_URL}api/v1/login`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ username, password }),
    });
    if (!res.ok) {
      let detail = `Login failed (${res.status})`;
      try {
        const body = await res.json();
        if (typeof body.detail === "string") detail = body.detail;
      } catch {
        // body wasn't JSON; keep the status-code message
      }
      throw new Error(detail);
    }
    const body = (await res.json()) as { token: string };
    setToken(body.token);
  }, []);

  const signOut = useCallback(() => {
    setToken(null);
    setIdentity(null);
  }, []);
  const onUnauthorized = useCallback(() => {
    setToken(null);
    setIdentity(null);
  }, []);

  const groups = identity?.groups ?? [];
  const mustChangePassword = groups.includes(GROUP_PASSWORD_RESET);
  // A user owing a rotation carries ONLY `pwreset`, never their role, so there
  // is no role to report until they have rotated.
  const role = mustChangePassword
    ? null
    : (groups.find((g) => g === "admin" || g === "viewer") ?? null);
  const mcpScanEnabled = identity?.mcpScanEnabled ?? false;

  const value = useMemo<AuthState>(
    () => ({
      token,
      identity,
      identityLoading,
      role,
      mcpScanEnabled,
      mustChangePassword,
      signIn,
      signOut,
      refreshIdentity,
      onUnauthorized,
    }),
    [
      token,
      identity,
      identityLoading,
      role,
      mcpScanEnabled,
      mustChangePassword,
      signIn,
      signOut,
      refreshIdentity,
      onUnauthorized,
    ],
  );

  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}

export function useAuth(): AuthState {
  const v = useContext(Ctx);
  if (!v) throw new Error("useAuth must be used inside <AuthProvider>");
  return v;
}
